import React, { useState, useEffect } from "react";
import {
  MainContent,
  MixerCard,
  FormContainer,
  InputGroup,
  Label,
  InputWithError,
  Input,
  ErrorMessage,
  ActionButton,
  DetailCard,
  DetailLabel,
  DetailValue,
} from "../components/styles";
import { Navigation } from "../components/Navigation";
import { useAnimation } from "../components/AnimationContext";
import { useWebSocket } from "../components/WebSocketContext";
import { ProgressSteps } from "../components/ProgressSteps";
import { getAddress } from "ethers";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.obscurium.pro";
const X_API_KEY = process.env.REACT_APP_X_API_KEY || "35ae663864699ef4409b93ad7440890257b25b06a8299b1823dc4a31e9dccf73";

// API helper functions
const getAuthToken = (): string | null => {
  return process.env.REACT_APP_API_KEY || null;
};

export const ProgressPage: React.FC = () => {
  const { disableAnimations } = useAnimation();
  const {
    subscribeToTransfer,
    unsubscribeFromTransfer,
    onProgress,
    onCompleted,
    onFailed,
  } = useWebSocket();
  const [senderAddress, setSenderAddress] = useState("");
  const [transfers, setTransfers] = useState<any[]>([]);
  const [transfersLoading, setTransfersLoading] = useState(false);
  const [transfersError, setTransfersError] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Subscribe to WebSocket updates for all loaded transfers
  useEffect(() => {
    if (transfers.length === 0) return;

    // Subscribe to each transfer
    transfers.forEach((transfer) => {
      const tid = transfer.id || transfer.transfer_id;
      if (tid) {
        subscribeToTransfer(tid);
      }
    });

    // Listen for progress updates
    const unsubProgress = onProgress((progress) => {
      setTransfers((prevTransfers) =>
        prevTransfers.map((transfer) => {
          const tid = transfer.id || transfer.transfer_id;
          if (tid === progress.transferId) {
            return {
              ...transfer,
              status: progress.status,
              progress: { steps: progress.visualSteps },
              binance_withdrawn_amount:
                progress.withdrawnAmount || transfer.binance_withdrawn_amount,
              current_confirms: progress.currentConfirms,
              required_confirms: progress.requiredConfirms,
              estimated_unlock_seconds: progress.estimatedUnlockSeconds,
              estimated_completion_seconds: progress.estimatedCompletionSeconds,
              total_current_confirms: progress.totalCurrentConfirms,
              total_required_confirms: progress.totalRequiredConfirms,
            };
          }
          return transfer;
        })
      );
    });

    // Listen for completion
    const unsubCompleted = onCompleted((data) => {
      setTransfers((prevTransfers) =>
        prevTransfers.map((transfer) => {
          const tid = transfer.id || transfer.transfer_id;
          if (tid === data.transferId) {
            return {
              ...transfer,
              status: "completed",
              final_tx_hash: data.finalTxHash,
            };
          }
          return transfer;
        })
      );
    });

    // Listen for failures
    const unsubFailed = onFailed((data) => {
      setTransfers((prevTransfers) =>
        prevTransfers.map((transfer) => {
          const tid = transfer.id || transfer.transfer_id;
          if (tid === data.transferId) {
            return {
              ...transfer,
              status: "failed",
              failure_reason: data.error,
            };
          }
          return transfer;
        })
      );
    });

    return () => {
      // Unsubscribe from all transfers
      transfers.forEach((transfer) => {
        const tid = transfer.id || transfer.transfer_id;
        if (tid) {
          unsubscribeFromTransfer(tid);
        }
      });
      unsubProgress();
      unsubCompleted();
      unsubFailed();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    transfers.length,
    subscribeToTransfer,
    unsubscribeFromTransfer,
    onProgress,
    onCompleted,
    onFailed,
  ]);

  // Polling fallback every 30 seconds to update transfer status (WebSocket backup)
  useEffect(() => {
    if (transfers.length === 0) return;

    // Check if any transfers are still in progress
    const hasActiveTransfers = transfers.some(
      (t) =>
        t.status !== "completed" &&
        t.status !== "failed" &&
        t.status !== "COMPLETED" &&
        t.status !== "FAILED"
    );

    if (!hasActiveTransfers) return;

    const fetchAllTransferStatuses = async () => {
      const token = getAuthToken();
      if (!token) return;

      try {
        const updatePromises = transfers.map(async (transfer) => {
          const tid = transfer.id || transfer.transfer_id;
          if (!tid) return transfer;

          // Skip if already completed or failed
          if (
            transfer.status === "completed" ||
            transfer.status === "failed" ||
            transfer.status === "COMPLETED" ||
            transfer.status === "FAILED"
          ) {
            return transfer;
          }

          try {
            const response = await fetch(
              `${API_BASE_URL}/v1/transfer/status/${tid}`,
              {
                headers: { Authorization: `Bearer ${token}`, "X-API-KEY": X_API_KEY },
              }
            );

            const data = await response.json();

            if (data.status === "success" && data.data) {
              return data.data;
            }
          } catch (error) {
            console.error(`Error fetching status for ${tid}:`, error);
          }
          return transfer;
        });

        const updatedTransfers = await Promise.all(updatePromises);
        setTransfers(updatedTransfers);
      } catch (error) {
        console.error("Error polling transfer statuses:", error);
      }
    };

    // Poll every 30 seconds
    const interval = setInterval(fetchAllTransferStatuses, 30000);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transfers.length]);

  const validateQueryInput = (input: string): string => {
    if (!input.trim()) {
      return "Sender address or transfer ID is required";
    }

    // Check if it's a transfer ID
    const isTransferId = input.startsWith("transfer_");

    if (isTransferId) {
      return ""; // Transfer ID is valid
    }

    // Check if it's an EVM address (Ethereum, Base, Arbitrum, BSC, etc.)
    const isEvmAddress = /^0x[a-fA-F0-9]{40}$/.test(input);

    // Check if it's a Solana address (base58, 32-44 characters)
    const isSolanaAddress = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(input);

    if (!isEvmAddress && !isSolanaAddress && !isTransferId) {
      return "Invalid format (must be EVM address, Solana address, or transfer ID)";
    }

    return "";
  };

  // Helper to check if address is EVM format
  const isEvmAddress = (address: string): boolean => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  // Get transfers and swaps by sender address or transfer ID
  const handleGetTransfers = async () => {
    const queryErr = validateQueryInput(senderAddress);

    setTransfersError(queryErr);
    setTransfers([]);
    setSearchPerformed(false);

    if (queryErr) {
      console.log("❌ Query validation failed:", queryErr);
      return;
    }

    setTransfersLoading(true);
    setTransfersError("");

    try {
      const token = getAuthToken();
      if (!token) {
        setTransfersError("Authentication failed");
        return;
      }

      // Check if query is a transfer ID
      const isTransferId = senderAddress.startsWith("transfer_");

      if (isTransferId) {
        // Query by transfer ID - only fetch transfers
        const transfersResponse = await fetch(
          `${API_BASE_URL}/v1/transfer/search/${senderAddress}`,
          {
            headers: { Authorization: `Bearer ${token}`, "X-API-KEY": X_API_KEY },
          }
        );

        const transfersData = await transfersResponse.json();

        if (transfersResponse.ok && transfersData.status === "success") {
          setTransfers(transfersData.data.transfers);
        } else {
          setTransfersError(transfersData.message || "Transfer not found");
        }
      } else {
        // Query by sender address - fetch both transfers and swaps
        const [transfersResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/v1/transfer/search/${senderAddress}`, {
            headers: { Authorization: `Bearer ${token}`, "X-API-KEY": X_API_KEY },
          }),
        ]);

        const [transfersData] = await Promise.all([transfersResponse.json()]);

        if (transfersResponse.ok && transfersData.status === "success") {
          setTransfers(transfersData.data.transfers);
        } else {
          console.warn("Failed to get transfers:", transfersData.message);
        }

        // If both failed, show error
        if (!transfersResponse.ok || transfersData.status !== "success") {
          setTransfersError("Failed to get transfers and swaps");
        }
      }
    } catch (error) {
      setTransfersError("Network error");
    } finally {
      setTransfersLoading(false);
      setSearchPerformed(true);
    }
  };

  return (
    <MainContent>
      <MixerCard $disableAnimation={disableAnimations}>
        <Navigation />
        <FormContainer $disableAnimation={disableAnimations}>
          <InputGroup>
            <Label>Search by sender address or transfer ID</Label>
            <InputWithError>
              <Input
                type="text"
                placeholder="Enter sender address or transfer ID (transfer_xxxxx)"
                value={senderAddress}
                $hasError={!!transfersError}
                onChange={(e) => {
                  setSenderAddress(e.target.value);
                  if (transfersError) setTransfersError("");
                }}
              />
              {transfersError && <ErrorMessage>{transfersError}</ErrorMessage>}
            </InputWithError>
          </InputGroup>

          <ActionButton
            onClick={handleGetTransfers}
            disabled={transfersLoading}
            $loading={transfersLoading}
          >
            {transfersLoading ? "Loading..." : "Get Transfers"}
          </ActionButton>

          {/* <InputGroup>
            <Label>Or Check Specific Transfer Status</Label>
            <InputWithError>
              <Input
                type="text"
                placeholder="Enter transfer ID (e.g., transfer_1234567890_abc123)"
                value={transferId}
                $hasError={!!statusError}
                onChange={(e) => {
                  setTransferId(e.target.value);
                  if (statusError) setStatusError('');
                }}
              />
              {statusError && (
                <ErrorMessage>
                  {statusError}
                </ErrorMessage>
              )}
            </InputWithError>
          </InputGroup> */}

          {/* <ActionButton
            onClick={handleCheckStatus}
            disabled={statusLoading}
            $loading={statusLoading}
          >
            {statusLoading ? 'Checking...' : 'Check Status'}
          </ActionButton> */}

          {/* No transfers found message */}
          {searchPerformed && transfers.length === 0 && !transfersError && (
            <DetailCard style={{ marginTop: "2rem" }}>
              <div
                style={{
                  padding: "2rem",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <DetailLabel
                  style={{
                    fontSize: "1.1rem",
                    marginBottom: "0.75rem",
                    textAlign: "center",
                  }}
                >
                  No transfers or swaps found
                </DetailLabel>
                <DetailValue
                  style={{
                    color: "rgba(255, 255, 255, 0.6)",
                    lineHeight: "1.5",
                    textAlign: "center",
                  }}
                >
                  No transfers were found for this address or transfer ID.
                  <br />
                  Make sure you entered the correct sender address or transfer
                  ID.
                </DetailValue>
              </div>
            </DetailCard>
          )}

          {transfers.length > 0 && (
            <div style={{ marginTop: "2rem" }}>
              {transfers.length > 0 && (
                <>
                  <Label>Your Transfers ({transfers.length})</Label>
                  <div
                    style={{ display: "grid", gap: "1rem", marginTop: "1rem" }}
                  >
                    {transfers.map((transfer) => (
                      <DetailCard key={transfer.id}>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "1rem",
                          }}
                        >
                          <div>
                            <DetailLabel>Transfer ID</DetailLabel>
                            <DetailValue
                              style={{
                                fontSize: "0.75rem",
                                fontFamily: "monospace",
                              }}
                            >
                              {transfer.id}
                            </DetailValue>
                          </div>
                          <div>
                            <DetailLabel>Status</DetailLabel>
                            <DetailValue
                              style={{
                                color:
                                  transfer.status === "completed"
                                    ? "#10b981"
                                    : transfer.status === "failed"
                                      ? "#ef4444"
                                      : "#f59e0b",
                                fontWeight: 600,
                              }}
                            >
                              {transfer.status.toUpperCase()}
                            </DetailValue>
                          </div>
                          <div>
                            <DetailLabel>From</DetailLabel>
                            <DetailValue>
                              {transfer.origin_chain}: {transfer.amount}
                            </DetailValue>
                          </div>
                          <div>
                            <DetailLabel>To (Estimated)</DetailLabel>
                            <DetailValue>
                              {transfer.binance_withdrawn_amount
                                ? `${transfer.destination_chain}: ${parseFloat(
                                  transfer.binance_withdrawn_amount
                                ).toFixed(6)}`
                                : `${transfer.destination_chain}: Calculating...`}
                            </DetailValue>
                          </div>
                        </div>

                        {/* Addresses Row - Side by Side */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "1rem",
                            marginTop: "0.5rem",
                          }}
                        >
                          <div>
                            <DetailLabel>Sender Address</DetailLabel>
                            <DetailValue
                              style={{
                                fontSize: "0.65rem",
                                fontFamily: "monospace",
                                wordBreak: "break-all",
                              }}
                            >
                              {transfer.sender_address &&
                                isEvmAddress(transfer.sender_address)
                                ? getAddress(transfer.sender_address)
                                : transfer.sender_address || "Pending..."}
                            </DetailValue>
                          </div>
                          <div>
                            <DetailLabel>Receiving Address</DetailLabel>
                            <DetailValue
                              style={{
                                fontSize: "0.65rem",
                                fontFamily: "monospace",
                                wordBreak: "break-all",
                              }}
                            >
                              {transfer.receiving_wallet_address &&
                                isEvmAddress(transfer.receiving_wallet_address)
                                ? getAddress(transfer.receiving_wallet_address)
                                : transfer.receiving_wallet_address}
                            </DetailValue>
                          </div>
                        </div>

                        {/* Estimated Time Section - Always show for active transfers */}
                        {transfer.status !== "completed" &&
                          transfer.status !== "failed" && (
                            <div
                              style={{
                                marginTop: "1rem",
                                padding: "0.75rem",
                                backgroundColor: "rgba(16, 185, 129, 0.08)",
                                borderRadius: "0.5rem",
                                border: "1px solid rgba(16, 185, 129, 0.2)",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "0.5rem",
                                }}
                              >
                                <div>
                                  <DetailLabel style={{ color: "#10b981" }}>
                                    Estimated Time to Complete
                                  </DetailLabel>
                                  <DetailValue
                                    style={{
                                      color: "#10b981",
                                      fontWeight: 600,
                                      fontSize: "1.1rem",
                                    }}
                                  >
                                    {transfer.estimated_completion_seconds &&
                                      transfer.estimated_completion_seconds > 0
                                      ? transfer.estimated_completion_seconds >= 3600
                                        ? `~${Math.floor(transfer.estimated_completion_seconds / 3600)}h ${Math.floor((transfer.estimated_completion_seconds % 3600) / 60)}m`
                                        : `~${Math.ceil(transfer.estimated_completion_seconds / 60)} min`
                                      : "Calculating..."}
                                  </DetailValue>
                                </div>
                                {/* {((transfer.total_current_confirms !== undefined &&
                                  transfer.total_required_confirms !== undefined &&
                                  transfer.total_required_confirms > 0) ||
                                  (transfer.current_confirms !== undefined &&
                                  transfer.required_confirms !== undefined &&
                                  transfer.required_confirms > 0)) && (
                                    <div style={{ textAlign: "right" }}>
                                      <DetailLabel style={{ color: "rgba(255, 255, 255, 0.6)" }}>
                                        Confirmations
                                      </DetailLabel>
                                      <DetailValue
                                        style={{
                                          fontSize: "0.9rem",
                                          color: "rgba(255, 255, 255, 0.8)",
                                        }}
                                      >
                                        {transfer.total_current_confirms !== undefined &&
                                        transfer.total_required_confirms !== undefined
                                          ? `${transfer.total_current_confirms}/${transfer.total_required_confirms}`
                                          : `${transfer.current_confirms}/${transfer.required_confirms}`}
                                      </DetailValue>
                                    </div>
                                  )} */}
                              </div>
                            </div>
                          )}
                        {transfer.status === "failed" &&
                          transfer.failure_reason && (
                            <div
                              style={{
                                marginTop: "1rem",
                                padding: "0.75rem",
                                backgroundColor: "rgba(239, 68, 68, 0.1)",
                                borderRadius: "0.5rem",
                                border: "1px solid rgba(239, 68, 68, 0.3)",
                              }}
                            >
                              <DetailLabel style={{ color: "#ef4444" }}>
                                Failure Reason
                              </DetailLabel>
                              <DetailValue
                                style={{
                                  color: "#ef4444",
                                  fontWeight: 500,
                                  textTransform: "capitalize",
                                }}
                              >
                                {transfer.failure_reason === "expired"
                                  ? "Deposit not detected after 15 minutes"
                                  : transfer.failure_reason}
                              </DetailValue>
                            </div>
                          )}
                        <div style={{ marginTop: "1rem" }}>
                          <DetailLabel>Creation date</DetailLabel>
                          <DetailValue>
                            {transfer.created_at
                              ? new Date(transfer.created_at).toLocaleString()
                              : ""}
                          </DetailValue>
                        </div>

                        {/* Visual Progress Steps */}
                        {transfer.progress && transfer.progress.steps && (
                          <ProgressSteps steps={transfer.progress.steps} />
                        )}
                      </DetailCard>
                    ))}
                  </div>
                </>
              )}

              {/* {swaps.length > 0 && (
                <>
                  {transfers.length > 0 && (
                    <div style={{ marginTop: "2rem" }} />
                  )}
                  <Label>Your Swaps ({swaps.length})</Label>
                  <div
                    style={{ display: "grid", gap: "1rem", marginTop: "1rem" }}
                  >
                    {swaps.map((swap) => (
                      <DetailCard key={swap.swap_id}>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "1rem",
                          }}
                        >
                          <div>
                            <DetailLabel>Swap ID</DetailLabel>
                            <DetailValue>{swap.swap_id}</DetailValue>
                          </div>
                          <div>
                            <DetailLabel>Status</DetailLabel>
                            <DetailValue
                              style={{
                                color:
                                  swap.status === "completed"
                                    ? "#00ff88"
                                    : swap.status === "failed"
                                    ? "#ff4757"
                                    : "#ffa500",
                              }}
                            >
                              {swap.status.toUpperCase()}
                            </DetailValue>
                          </div>
                          <div>
                            <DetailLabel>From → To</DetailLabel>
                            <DetailValue>
                              {swap.src_token} ({swap.src_network}) →{" "}
                              {swap.dst_token} ({swap.dst_network})
                            </DetailValue>
                          </div>
                          <div>
                            <DetailLabel>Amount</DetailLabel>
                            <DetailValue>
                              {swap.amount} {swap.src_token}
                            </DetailValue>
                          </div>
                          <div>
                            <DetailLabel>Deposit Address</DetailLabel>
                            <DetailValue style={{ fontSize: "0.8rem" }}>
                              {swap.deposit_wallet_address}
                            </DetailValue>
                          </div>
                          <div>
                            <DetailLabel>Receiving Address</DetailLabel>
                            <DetailValue style={{ fontSize: "0.8rem" }}>
                              {swap.dst_address}
                            </DetailValue>
                          </div>
                        </div>
                        <div style={{ marginTop: "1rem" }}>
                          <DetailLabel>Creation date</DetailLabel>
                          <DetailValue>
                            {new Date(swap.timestamp).toLocaleString()}
                          </DetailValue>
                        </div>
                      </DetailCard>
                    ))}
                  </div>
                </>
              )} */}
            </div>
          )}
        </FormContainer>
      </MixerCard>
    </MainContent>
  );
};
